import { useMemo } from "react";
import type { RefObject } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, Skeleton } from "@nexus/ui";
import { useTranslation } from "react-i18next";
import type { OverviewFilters } from "@nexus/contracts";
import type { MyBrandFiltersInput } from "@/lib/queries/dashboard.queries";
import { tenantSettingsQueryOptions } from "@/lib/queries/tenant-settings.queries";
import { useInfiniteScrollSentinel } from "../../hooks/useInfiniteScrollSentinel.js";
import { useTopicDetailExamples } from "../../hooks/useTopicDetailExamples.js";
import { useTenantCurrency } from "@/lib/use-tenant-currency";
import type { TopicDetailContext } from "../../lib/topicDetailContext.js";
import { humanizeBrandName } from "../../lib/humanizeBrandName.js";
import {
  formatTopicExampleCpa,
  formatTopicExampleRoas,
  formatTopicExampleSpend,
  topicExamplePreviewSubtitle,
} from "../../lib/topicExampleFormat.js";
import { ExampleCreativeGrid, type ExampleCardVm } from "../shared/ExampleCreativeGrid.js";

interface TopicDetailExamplesTabProps {
  tenantId: string;
  context: TopicDetailContext;
  overviewFilters: OverviewFilters;
  globalFilters: MyBrandFiltersInput;
  /** Drawer scroll container the creatives grid virtualizes against. */
  scrollContainerRef?: RefObject<HTMLElement | null>;
}

const SKELETON_CARDS = 8;

function ExamplesSkeleton() {
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
      {Array.from({ length: SKELETON_CARDS }).map((_, i) => (
        <div key={i} className="overflow-hidden rounded-md border">
          <Skeleton className="aspect-[4/5] rounded-none" />
          <div className="px-2.5 py-2">
            <Skeleton className="h-3.5 w-2/3" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function TopicDetailExamplesTab({
  tenantId,
  context,
  overviewFilters,
  globalFilters,
  scrollContainerRef,
}: TopicDetailExamplesTabProps) {
  const { t } = useTranslation();
  const { symbol: currencySymbol } = useTenantCurrency();
  const { data: tenantSettings } = useQuery(tenantSettingsQueryOptions(tenantId));
  const brandName = tenantSettings?.defaultBrand ?? "";

  const {
    rows,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
  } = useTopicDetailExamples(tenantId, overviewFilters, globalFilters, context);

  const sentinelRef = useInfiniteScrollSentinel({
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isError: isFetchNextPageError,
  });

  const cards = useMemo<ExampleCardVm[]>(
    () =>
      rows.map((row) => {
        const isVideo = row.objectType.toLowerCase() === "video";
        const adsMeta = t("overview.topicDetail.exampleAdsMeta", {
          defaultValue: "{{count}} ads",
          count: row.ads,
        });
        const meta = [row.datesActive, adsMeta].filter(Boolean).join(" · ");
        const hasAdLink = row.adLibUrl.startsWith("http");
        return {
          key: row.hash,
          thumbnailUrl: row.thumbnailUrl,
          src: isVideo ? row.creativeUrl : row.creativeUrl || row.thumbnailUrl,
          type: isVideo ? "video" : "image",
          title: brandName ? humanizeBrandName(brandName) : row.adName,
          subtitle: hasAdLink
            ? t("overview.topicDetail.viewAd", { defaultValue: "View ad" })
            : topicExamplePreviewSubtitle(row),
          subtitleHref: hasAdLink ? row.adLibUrl : undefined,
          meta,
          accentColor: row.scatterColor || undefined,
          metrics: [
            {
              label: t("overview.topicDetail.columnSpend", { defaultValue: "Spend" }),
              value: formatTopicExampleSpend(row, currencySymbol),
            },
            {
              label: t("overview.topicDetail.columnCpa", { defaultValue: "CPA" }),
              value: formatTopicExampleCpa(row, currencySymbol),
            },
            {
              label: t("overview.topicDetail.columnRoas", { defaultValue: "ROAS" }),
              value: formatTopicExampleRoas(row),
            },
          ],
        };
      }),
    [rows, currencySymbol, brandName, t],
  );

  const mainContent = (() => {
    if (isLoading) return <ExamplesSkeleton />;

    if (isError) {
      return (
        <p className="text-sm text-destructive">
          {t("overview.topicDetail.examplesError", { defaultValue: "Failed to load examples." })}
        </p>
      );
    }

    if (rows.length === 0) {
      return (
        <div className="space-y-1">
          <p className="text-sm text-muted-foreground">
            {t("overview.topicDetail.noExamples", { defaultValue: "No creatives match this topic." })}
          </p>
          <p className="text-xs text-muted-foreground">
            {t("overview.topicDetail.noExamplesHint", {
              defaultValue: "Try widening date range or filters, or pick another topic on the chart.",
            })}
          </p>
        </div>
      );
    }

    return (
      <>
        <ExampleCreativeGrid
          cards={cards}
          emptyLabel={t("overview.topicDetail.noExamples", { defaultValue: "No creatives match this topic." })}
          scrollContainerRef={scrollContainerRef}
        />

        {hasNextPage && (
          <div
            ref={sentinelRef}
            data-testid="topic-detail-examples-sentinel"
            className="flex items-center justify-center gap-2 py-3 text-xs text-muted-foreground"
          >
            {isFetchNextPageError ? (
              <>
                <span className="text-destructive">
                  {t("overview.topicDetail.loadMoreError", { defaultValue: "Couldn’t load more." })}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                >
                  {t("overview.topicDetail.retry", { defaultValue: "Retry" })}
                </Button>
              </>
            ) : (
              t("overview.topicDetail.loadingMore", { defaultValue: "Loading more…" })
            )}
          </div>
        )}

        <div className="flex items-center justify-between gap-3 border-t border-border pt-3">
          <p className="text-xs text-muted-foreground">
            {t("overview.topicDetail.loadedCount", { defaultValue: "{{count}} loaded", count: rows.length })}
          </p>
        </div>
      </>
    );
  })();

  return <div className="flex flex-col gap-4 px-6 py-5">{mainContent}</div>;
}
